/**
 * Approval access decision for the application shell.
 *
 * Kept as a pure function, separate from the React guard, for the same two
 * reasons `features/admin/adminAccess.ts` is: the decision is the part worth
 * testing, and `web` has no unit-test runner of its own (its tests run under
 * the `functions` vitest binary — see accountAccess.test.ts alongside this
 * file).
 *
 * THIS IS A VISIBILITY CONTROL, NOT A SECURITY BOUNDARY. Anything shipped to a
 * browser can be read and modified by whoever has it. Enforcement is
 * server-side, in `assertAccountApproved` (functions/src/auth/accountApproval.ts),
 * which every work callable reaches independently of anything decided here.
 * What this buys is that a pending account gets a screen explaining its
 * situation, instead of a dashboard that fails on every click.
 *
 * THE RULES MIRROR THE SERVER EXACTLY, deliberately. Two authorities that
 * disagree about who may use the platform is worse than either one alone:
 *   - admins are never gated, whatever the status says;
 *   - a status that could not be READ fails OPEN;
 *   - anything short of a read `approved` is treated as pending.
 */

/**
 * What the account's status is known to be.
 *
 * `absent`, `unknown` and `unreadable` are three different things and the
 * distinction is load-bearing:
 *   - `absent`     — the read succeeded and there is no document.
 *   - `unknown`    — the document exists but carries no usable status. This is
 *                    a real state, not a defensive one: the signup screen
 *                    writes the application fields and firestore.rules forbids
 *                    it from writing `status`, so between that write and the
 *                    onUserCreated trigger the document exists without one.
 *   - `unreadable` — the read itself failed. Nothing is known, so nothing may
 *                    be concluded, so the account is let through.
 */
export type AccountStatus =
  | 'approved'
  | 'pending'
  | 'rejected'
  | 'absent'
  | 'unknown'
  | 'unreadable';

export interface AccountAccessInput {
  /** True until the session, the admin claim AND the status have resolved. */
  loading: boolean;
  signedIn: boolean;
  /** Whether the resolved ID token carries `admin: true`. */
  isAdmin: boolean;
  status: AccountStatus;
}

/**
 * - `wait`        — nothing may render yet.
 * - `login`       — no one is signed in.
 * - `application` — signed in, not approved: show the application screen.
 * - `allow`       — signed in and entitled to the app.
 */
export type AccountAccessDecision = 'wait' | 'login' | 'application' | 'allow';

/**
 * Order matters, and matches `resolveAdminAccess`. `loading` is checked FIRST
 * and unconditionally: a shell that renders and then redirects has already
 * shown the thing the gate exists to hide, which is precisely the "no dashboard
 * flash after signup" requirement.
 */
export function resolveAccountAccess(input: AccountAccessInput): AccountAccessDecision {
  if (input.loading) return 'wait';
  if (!input.signedIn) return 'login';

  // Admins are never gated by approval status. Same rule as the server, for
  // the same reason: an administrator who cannot reach the administration
  // screen cannot approve anybody, including themselves.
  if (input.isAdmin) return 'allow';

  // FAIL OPEN — deliberate. A transient Firestore error must never present
  // every approved customer with an application screen they cannot get past.
  // An account that cannot be PROVEN pending or rejected is treated as
  // approved.
  if (input.status === 'unreadable') return 'allow';

  return input.status === 'approved' ? 'allow' : 'application';
}

/**
 * Map a users/{uid} snapshot to a status.
 *
 * `undefined` / `null` means the document does not exist. A document that
 * exists but whose `status` is not one of the three known values maps to
 * `unknown`, NOT to `approved` — the comparison is exact and case-sensitive,
 * matching the server's `status === 'approved'`.
 *
 * The `unreadable` case never comes from here; it is what the caller supplies
 * when the read itself failed.
 */
export function accountStatusFromDoc(
  data: Record<string, unknown> | null | undefined,
): AccountStatus {
  if (!data) return 'absent';
  const status = data.status;
  if (status === 'approved' || status === 'pending' || status === 'rejected') {
    return status;
  }
  return 'unknown';
}
